import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import {
  Finding,
  FindingStatus,
  VerificationResult,
  EffectivenessResult,
} from '../types';
import { findingService } from '../findingService';

interface FindingDetailModalProps {
  finding: Finding;
  onClose: () => void;
  onRefresh: () => void;
}

export const FindingDetailModal: React.FC<FindingDetailModalProps> = ({
  finding: initialFinding,
  onClose,
  onRefresh,
}) => {
  const [finding, setFinding] = useState<Finding>(initialFinding);
  const [activeTab, setActiveTab] = useState<'CORRECTION_RCA' | 'VERIFICATION_EFF' | 'REQUIREMENTS_EVI' | 'TIMELINE'>('CORRECTION_RCA');
  const [loading, setLoading] = useState(false);

  // Correction Form
  const [showAddCorrection, setShowAddCorrection] = useState(false);
  const [corrDesc, setCorrDesc] = useState('');
  const [corrContainment, setCorrContainment] = useState('');
  const [corrTakenBy, setCorrTakenBy] = useState(finding.ownerName);

  // Root Cause Form
  const [showAddRootCause, setShowAddRootCause] = useState(false);
  const [rcMethod, setRcMethod] = useState<'FIVE_WHY' | 'FISHBONE' | 'BARRIER_ANALYSIS' | 'HFACS'>('FIVE_WHY');
  const [rcType, setRcType] = useState('PROCESS');
  const [rcDesc, setRcDesc] = useState('');

  // Verification Form
  const [showAddVerification, setShowAddVerification] = useState(false);
  const [verifId, setVerifId] = useState(finding.verifierId || 'AUDITOR-INDEPENDENT');
  const [verifName, setVerifName] = useState(finding.verifierName || 'Independent Auditor');
  const [verifResult, setVerifResult] = useState<VerificationResult>('EFFECTIVE');
  const [verifNotes, setVerifNotes] = useState('');

  // Effectiveness Form
  const [showAddEffectiveness, setShowAddEffectiveness] = useState(false);
  const [effDate, setEffDate] = useState(new Date().toISOString().split('T')[0]);
  const [effResult, setEffResult] = useState<EffectivenessResult>('EFFECTIVE');
  const [effNotes, setEffNotes] = useState('');

  // Extension Form
  const [showExtensionModal, setShowExtensionModal] = useState(false);
  const [extNewDate, setExtNewDate] = useState(new Date(Date.now() + 15 * 86400000).toISOString().split('T')[0]);
  const [extReason, setExtReason] = useState('');

  // Requirement Form
  const [showAddReq, setShowAddReq] = useState(false);
  const [reqStandard, setReqStandard] = useState('EASA Part-CAMO.A.305');
  const [reqClause, setReqClause] = useState('CAMO.A.305(b)');
  const [reqText, setReqText] = useState('');

  // Close finding comment
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [closeComment, setCloseComment] = useState('All corrective actions and independent verifications successfully completed.');

  // Reopen finding reason
  const [showReopenModal, setShowReopenModal] = useState(false);
  const [reopenReason, setReopenReason] = useState('');

  const isClosed = finding.status === 'CLOSED';

  const refreshDetails = async () => {
    try {
      setLoading(true);
      const data = await findingService.getFindingById(finding.id);
      setFinding(data);
    } catch (err) {
      console.error('Error fetching finding details:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshDetails();
  }, [finding.id]);

  const handleStatusTransition = async (toStatus: FindingStatus, reason: string) => {
    try {
      setLoading(true);
      await findingService.transitionStatus(finding.id, toStatus, reason);
      await refreshDetails();
      onRefresh();
    } catch (err: any) {
      alert('Failed to update status: ' + (err?.message || err));
    } finally {
      setLoading(false);
    }
  };

  const handleAddCorrection = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!corrDesc.trim()) return;
    try {
      setLoading(true);
      await findingService.addCorrection(finding.id, {
        description: corrDesc,
        containmentAction: corrContainment.trim() || undefined,
        takenBy: corrTakenBy,
      });
      setShowAddCorrection(false);
      setCorrDesc('');
      setCorrContainment('');
      await refreshDetails();
      onRefresh();
    } catch (err: any) {
      alert('Failed to add correction: ' + (err?.message || err));
    } finally {
      setLoading(false);
    }
  };

  const handleAddRootCause = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!rcDesc.trim()) return;
    try {
      setLoading(true);
      await findingService.addRootCause(finding.id, {
        method: rcMethod as any,
        rootCauseType: rcType as any,
        description: rcDesc,
        verifiedBy: 'QUALITY_SPECIALIST',
      });
      setShowAddRootCause(false);
      setRcDesc('');
      await refreshDetails();
      onRefresh();
    } catch (err: any) {
      alert('Failed to add root cause: ' + (err?.message || err));
    } finally {
      setLoading(false);
    }
  };

  const handleAddVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!verifNotes.trim()) return;
    try {
      setLoading(true);
      await findingService.addVerification(finding.id, {
        verifierId: verifId,
        verifierName: verifName,
        result: verifResult,
        notes: verifNotes,
      });
      setShowAddVerification(false);
      setVerifNotes('');
      await refreshDetails();
      onRefresh();
    } catch (err: any) {
      alert('Failed to record verification: ' + (err?.message || err));
    } finally {
      setLoading(false);
    }
  };

  const handleAddEffectiveness = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!effNotes.trim()) return;
    try {
      setLoading(true);
      await findingService.addEffectiveness(finding.id, {
        evaluationDate: effDate,
        evaluatorId: 'QUALITY_DIRECTOR',
        evaluatorName: 'Quality Director',
        result: effResult,
        evidenceNotes: effNotes,
      });
      setShowAddEffectiveness(false);
      setEffNotes('');
      await refreshDetails();
      onRefresh();
    } catch (err: any) {
      alert('Failed to record effectiveness evaluation: ' + (err?.message || err));
    } finally {
      setLoading(false);
    }
  };

  const handleRequestExtension = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!extReason.trim()) return;
    try {
      setLoading(true);
      await findingService.requestExtension(finding.id, {
        newDueDate: extNewDate,
        reason: extReason,
        requestedBy: finding.ownerName,
      });
      setShowExtensionModal(false);
      setExtReason('');
      await refreshDetails();
      onRefresh();
    } catch (err: any) {
      alert('Failed to request deadline extension: ' + (err?.message || err));
    } finally {
      setLoading(false);
    }
  };

  const handleApproveExtension = async (extId: string) => {
    try {
      setLoading(true);
      await findingService.approveExtension(extId, 'QUALITY_DIRECTOR');
      await refreshDetails();
      onRefresh();
    } catch (err: any) {
      alert('Failed to approve extension: ' + (err?.message || err));
    } finally {
      setLoading(false);
    }
  };

  const handleCloseFinding = async () => {
    try {
      setLoading(true);
      await findingService.closeFinding(finding.id, 'QUALITY_DIRECTOR', closeComment);
      setShowCloseModal(false);
      await refreshDetails();
      onRefresh();
    } catch (err: any) {
      alert('Failed to close finding: ' + (err?.message || err));
    } finally {
      setLoading(false);
    }
  };

  const handleReopenFinding = async () => {
    if (!reopenReason.trim()) return;
    try {
      setLoading(true);
      await findingService.reopenFinding(finding.id, 'QUALITY_DIRECTOR', reopenReason);
      setShowReopenModal(false);
      setReopenReason('');
      await refreshDetails();
      onRefresh();
    } catch (err: any) {
      alert('Failed to reopen finding: ' + (err?.message || err));
    } finally {
      setLoading(false);
    }
  };

  const handleAddRequirement = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reqText.trim()) return;
    try {
      setLoading(true);
      await findingService.addRequirement(finding.id, {
        standard: reqStandard,
        clause: reqClause,
        requirementText: reqText,
      });
      setShowAddReq(false);
      setReqText('');
      await refreshDetails();
      onRefresh();
    } catch (err: any) {
      alert('Failed to link requirement: ' + (err?.message || err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-card modal-xl" style={{ height: '92vh' }}>
        {/* Header */}
        <div className="modal-header" style={{ background: '#0f172a', color: '#ffffff', padding: '20px 28px' }}>
          <div className="modal-header-content">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <span style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 700, background: '#2563eb', color: '#ffffff', padding: '2px 8px', borderRadius: '4px' }}>
                {finding.findingNumber}
              </span>
              <span style={{ fontSize: '11px', fontWeight: 700, background: '#f59e0b', color: '#0f172a', padding: '2px 8px', borderRadius: '4px' }}>
                {finding.severity}
              </span>
              <span style={{ fontSize: '11px', fontWeight: 600, background: '#334155', color: '#e2e8f0', padding: '2px 8px', borderRadius: '4px' }}>
                {finding.findingType}
              </span>
              <span style={{ fontSize: '11px', fontWeight: 700, background: '#1e293b', color: '#ffffff', border: '1px solid #475569', padding: '2px 8px', borderRadius: '4px' }}>
                {finding.status}
              </span>
            </div>
            <h2 style={{ fontSize: '19px', fontWeight: 700, color: '#ffffff', margin: '0 0 6px 0', lineHeight: 1.3 }}>{finding.title}</h2>
            <div style={{ fontSize: '12px', color: '#94a3b8', display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
              <span>Department: <strong style={{ color: '#f8fafc' }}>{finding.department}</strong></span>
              <span>Source: <strong style={{ color: '#38bdf8' }}>{finding.sourceType} {finding.sourceReference ? `(${finding.sourceReference})` : ''}</strong></span>
              <span>Owner: <strong style={{ color: '#f8fafc' }}>{finding.ownerName}</strong></span>
              <span>Due Date: <strong style={{ color: '#fbbf24', fontFamily: 'monospace' }}>{finding.dueDate}</strong></span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="modal-close-btn"
            style={{ color: '#94a3b8' }}
            title="Kapat"
            aria-label="Kapat"
          >
            <X size={20} />
          </button>
        </div>

        {/* Action Toolbar */}
        <div className="bg-gray-50 border-b border-gray-200 px-5 py-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            {!isClosed && (
              <>
                {finding.status === 'IDENTIFIED' && (
                  <button
                    onClick={() => handleStatusTransition('VALIDATED', 'Initial review completed and finding validated')}
                    disabled={loading}
                    className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-lg shadow-xs"
                  >
                    ✓ Validate Finding
                  </button>
                )}

                {finding.status === 'VALIDATED' && (
                  <button
                    onClick={() => handleStatusTransition('ASSIGNED', 'Assigned to action owner')}
                    disabled={loading}
                    className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-lg shadow-xs"
                  >
                    → Assign to Owner
                  </button>
                )}

                {['ASSIGNED', 'CORRECTION_REQUIRED', 'ROOT_CAUSE_ANALYSIS', 'CAPA_PLANNING'].includes(finding.status) && (
                  <button
                    onClick={() => handleStatusTransition('ACTION_IN_PROGRESS', 'Corrective actions initiated')}
                    disabled={loading}
                    className="px-3 py-1.5 bg-yellow-600 hover:bg-yellow-700 text-white text-xs font-bold rounded-lg shadow-xs"
                  >
                    ▶ Start Action (In Progress)
                  </button>
                )}

                {finding.status === 'ACTION_IN_PROGRESS' && (
                  <button
                    onClick={() => handleStatusTransition('PENDING_VERIFICATION', 'Actions completed, submitted for verification')}
                    disabled={loading}
                    className="px-3 py-1.5 bg-orange-600 hover:bg-orange-700 text-white text-xs font-bold rounded-lg shadow-xs"
                  >
                    ✓ Submit for Verification
                  </button>
                )}

                <button
                  onClick={() => setShowExtensionModal(true)}
                  disabled={loading}
                  className="px-3 py-1.5 bg-purple-50 hover:bg-purple-100 text-purple-700 border border-purple-200 text-xs font-bold rounded-lg"
                >
                  ⏱ Request Extension
                </button>

                <button
                  onClick={() => setShowCloseModal(true)}
                  disabled={loading}
                  className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg shadow-xs"
                >
                  🔒 Close Finding
                </button>
              </>
            )}

            {isClosed && (
              <button
                onClick={() => setShowReopenModal(true)}
                disabled={loading}
                className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-bold rounded-lg shadow-xs"
              >
                🔓 Reopen Finding
              </button>
            )}
          </div>

          <div className="flex items-center gap-2 text-xs">
            {isClosed ? (
              <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 font-bold rounded">
                CLOSED (READ ONLY)
              </span>
            ) : (
              <span className="font-semibold text-gray-600">
                Verification Status: <strong className="text-gray-900">{finding.verificationStatus}</strong>
              </span>
            )}
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-gray-200 px-5 bg-white space-x-6">
          <button
            onClick={() => setActiveTab('CORRECTION_RCA')}
            className={`py-3 text-xs font-bold border-b-2 transition ${
              activeTab === 'CORRECTION_RCA'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Immediate Corrections & RCA ({finding.corrections?.length || 0} / {finding.rootCauses?.length || 0})
          </button>
          <button
            onClick={() => setActiveTab('VERIFICATION_EFF')}
            className={`py-3 text-xs font-bold border-b-2 transition ${
              activeTab === 'VERIFICATION_EFF'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Independent Verification & Effectiveness ({finding.verifications?.length || 0})
          </button>
          <button
            onClick={() => setActiveTab('REQUIREMENTS_EVI')}
            className={`py-3 text-xs font-bold border-b-2 transition ${
              activeTab === 'REQUIREMENTS_EVI'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Governing Standards & Clauses ({finding.requirements?.length || 0})
          </button>
          <button
            onClick={() => setActiveTab('TIMELINE')}
            className={`py-3 text-xs font-bold border-b-2 transition ${
              activeTab === 'TIMELINE'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Audit Trail & History ({finding.statusHistory?.length || 0})
          </button>
        </div>

        {/* Tab Body */}
        <div className="p-5 flex-1 overflow-y-auto space-y-4 bg-gray-50/30">
          {/* TAB 1: CORRECTION & ROOT CAUSE */}
          {activeTab === 'CORRECTION_RCA' && (
            <div className="space-y-5">
              {/* Finding Statement Card */}
              <div className="bg-amber-50/60 p-4 rounded-lg border border-amber-200">
                <span className="text-[11px] font-bold text-amber-800 uppercase tracking-wide">Finding Statement</span>
                <p className="text-sm font-semibold text-gray-900 mt-1">{finding.statement}</p>
                {finding.description && (
                  <p className="text-xs text-gray-600 mt-1">{finding.description}</p>
                )}
              </div>

              {/* Immediate Corrections Section */}
              <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-xs space-y-3">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-sm font-bold text-gray-900">Immediate Corrections & Temporary Mitigations</h3>
                    <p className="text-xs text-gray-500">Containment actions taken immediately prior to formal root cause analysis.</p>
                  </div>
                  {!isClosed && (
                    <button
                      onClick={() => setShowAddCorrection(true)}
                      className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded"
                    >
                      + Add Immediate Correction
                    </button>
                  )}
                </div>

                {(!finding.corrections || finding.corrections.length === 0) ? (
                  <div className="p-4 text-center text-xs text-gray-400 bg-gray-50 rounded border border-dashed">
                    No immediate containment actions registered yet.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {finding.corrections.map((c) => (
                      <div key={c.id} className="p-3 bg-gray-50 rounded border border-gray-200 space-y-1">
                        <div className="flex justify-between items-center text-xs">
                          <span className="font-semibold text-gray-800">{c.takenBy}</span>
                          <span className="text-gray-400 font-mono">{c.takenAt}</span>
                        </div>
                        <p className="text-xs text-gray-700 font-medium">{c.description}</p>
                        {c.containmentAction && (
                          <p className="text-xs text-amber-800 bg-amber-50 p-1.5 rounded">
                            <strong>Containment Action:</strong> {c.containmentAction}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Root Cause Analysis Section */}
              <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-xs space-y-3">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-sm font-bold text-gray-900">Root Cause Analysis (RCA)</h3>
                    <p className="text-xs text-gray-500">Systemic analysis using 5-Why, Ishikawa Fishbone, or Barrier Analysis methodology.</p>
                  </div>
                  {!isClosed && (
                    <button
                      onClick={() => setShowAddRootCause(true)}
                      className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded"
                    >
                      + Add Root Cause
                    </button>
                  )}
                </div>

                {(!finding.rootCauses || finding.rootCauses.length === 0) ? (
                  <div className="p-4 text-center text-xs text-gray-400 bg-gray-50 rounded border border-dashed">
                    Root cause analysis has not been completed yet.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {finding.rootCauses.map((rc) => (
                      <div key={rc.id} className="p-3 bg-purple-50/30 rounded border border-purple-200 space-y-1">
                        <div className="flex justify-between items-center text-xs">
                          <span className="font-bold text-purple-900 bg-purple-100 px-2 py-0.5 rounded">
                            Method: {rc.method} | Category: {rc.rootCauseType}
                          </span>
                          <span className="text-gray-400 font-mono">{rc.completedAt}</span>
                        </div>
                        <p className="text-xs text-gray-800 font-medium">{rc.description}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* TAB 2: VERIFICATION & EFFECTIVENESS */}
          {activeTab === 'VERIFICATION_EFF' && (
            <div className="space-y-5">
              {/* Verification Section */}
              <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-xs space-y-3">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-sm font-bold text-gray-900">Independent Verification</h3>
                    <p className="text-xs text-gray-500">
                      Segregation of Duties (SoD): Finding / action owner cannot verify their own non-conformance.
                    </p>
                  </div>
                  {!isClosed && (
                    <button
                      onClick={() => setShowAddVerification(true)}
                      className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded"
                    >
                      + Perform Independent Verification
                    </button>
                  )}
                </div>

                {(!finding.verifications || finding.verifications.length === 0) ? (
                  <div className="p-4 text-center text-xs text-gray-400 bg-gray-50 rounded border border-dashed">
                    No independent verification record logged yet.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {finding.verifications.map((v) => (
                      <div key={v.id} className="p-3 bg-gray-50 rounded border border-gray-200 space-y-1">
                        <div className="flex justify-between items-center text-xs">
                          <span className="font-bold text-gray-900">{v.verifierName}</span>
                          <span
                            className={`px-2 py-0.5 rounded font-bold ${
                              v.result === 'EFFECTIVE'
                                ? 'bg-emerald-100 text-emerald-800'
                                : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {v.result}
                          </span>
                        </div>
                        <p className="text-xs text-gray-700">{v.notes}</p>
                        <div className="text-[11px] font-mono text-gray-400">{v.verifiedAt}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Effectiveness Evaluation Section */}
              <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-xs space-y-3">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-sm font-bold text-gray-900">Effectiveness Evaluation</h3>
                    <p className="text-xs text-gray-500">
                      Periodic audit to ensure corrective measures prevented systemic recurrence.
                    </p>
                  </div>
                  {!isClosed && (
                    <button
                      onClick={() => setShowAddEffectiveness(true)}
                      className="px-3 py-1.5 bg-teal-600 hover:bg-teal-700 text-white text-xs font-bold rounded"
                    >
                      + Evaluate Effectiveness
                    </button>
                  )}
                </div>

                {(!finding.effectiveness || finding.effectiveness.length === 0) ? (
                  <div className="p-4 text-center text-xs text-gray-400 bg-gray-50 rounded border border-dashed">
                    No effectiveness evaluation logged yet.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {finding.effectiveness.map((eff) => (
                      <div key={eff.id} className="p-3 bg-teal-50/20 rounded border border-teal-200 space-y-1">
                        <div className="flex justify-between items-center text-xs">
                          <span className="font-bold text-teal-900">{eff.evaluatorName}</span>
                          <span className="px-2 py-0.5 rounded font-bold bg-teal-100 text-teal-800">
                            {eff.result}
                          </span>
                        </div>
                        <p className="text-xs text-gray-700">{eff.evidenceNotes}</p>
                        <div className="text-[11px] font-mono text-gray-400">{eff.evaluationDate}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* TAB 3: REQUIREMENTS & EVIDENCE */}
          {activeTab === 'REQUIREMENTS_EVI' && (
            <div className="space-y-5">
              <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-xs space-y-3">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-sm font-bold text-gray-900">Violated Standards & Regulatory Clauses</h3>
                    <p className="text-xs text-gray-500">EASA, ICAO, FAA, IOSA, or Company Manual clauses.</p>
                  </div>
                  {!isClosed && (
                    <button
                      onClick={() => setShowAddReq(true)}
                      className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded"
                    >
                      + Add Requirement Clause
                    </button>
                  )}
                </div>

                {(!finding.requirements || finding.requirements.length === 0) ? (
                  <div className="p-4 text-center text-xs text-gray-400 bg-gray-50 rounded border border-dashed">
                    No regulatory requirements linked yet.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {finding.requirements.map((req) => (
                      <div key={req.id} className="p-3 bg-gray-50 rounded border border-gray-200 space-y-1">
                        <div className="flex justify-between text-xs font-bold text-gray-800">
                          <span>{req.standard}</span>
                          <span className="font-mono text-blue-600">{req.clause}</span>
                        </div>
                        <p className="text-xs text-gray-600">{req.requirementText}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* TAB 4: TIMELINE & EXTENSIONS */}
          {activeTab === 'TIMELINE' && (
            <div className="space-y-5">
              {/* Extensions list */}
              {finding.extensions && finding.extensions.length > 0 && (
                <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-xs space-y-3">
                  <h3 className="text-sm font-bold text-gray-900">Deadline Extension Requests</h3>
                  <div className="space-y-2">
                    {finding.extensions.map((ext) => (
                      <div key={ext.id} className="p-3 bg-purple-50/30 rounded border border-purple-200 flex justify-between items-center text-xs">
                        <div>
                          <div className="font-semibold text-gray-900">
                            Previous: {ext.oldDueDate} → New: <strong className="text-purple-700 font-mono">{ext.newDueDate}</strong>
                          </div>
                          <div className="text-gray-600 mt-0.5">Justification: {ext.reason}</div>
                          <div className="text-[11px] text-gray-400 font-mono mt-0.5">Requested By: {ext.requestedBy} ({ext.requestedAt})</div>
                        </div>
                        <div>
                          {ext.status === 'REQUESTED' && !isClosed ? (
                            <button
                              onClick={() => handleApproveExtension(ext.id)}
                              className="px-3 py-1 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded"
                            >
                              Approve Extension
                            </button>
                          ) : (
                            <span className="px-2 py-0.5 rounded font-bold bg-purple-100 text-purple-800">
                              {ext.status}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Status history timeline */}
              <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-xs space-y-3">
                <h3 className="text-sm font-bold text-gray-900">Lifecycle Audit Trail & Status Timeline</h3>
                <div className="space-y-2">
                  {finding.statusHistory?.map((h) => (
                    <div key={h.id} className="p-2.5 bg-gray-50 rounded border border-gray-200 flex justify-between items-start text-xs">
                      <div>
                        <div className="font-semibold text-gray-900">
                          {h.fromStatus ? `${h.fromStatus} → ` : ''}
                          <span className="font-bold text-blue-700">{h.toStatus}</span>
                        </div>
                        {h.reason && <p className="text-gray-600 mt-0.5">{h.reason}</p>}
                        <span className="text-[11px] text-gray-400 font-mono">Action Performed By: {h.changedBy}</span>
                      </div>
                      <span className="text-gray-400 font-mono text-[11px]">{h.createdAt}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 bg-gray-50 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-5 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200 rounded-lg transition"
          >
            Close
          </button>
        </div>
      </div>

      {/* MODAL: Add Correction */}
      {showAddCorrection && (
        <div className="modal-backdrop">
          <form onSubmit={handleAddCorrection} className="modal-card max-w-lg">
            <div className="modal-header">
              <div>
                <h3>Immediate Correction Record</h3>
                <p>Record the containment action and immediate remediation taken</p>
              </div>
              <button type="button" onClick={() => setShowAddCorrection(false)} className="close-btn">✕</button>
            </div>
            <div className="modal-body space-y-4">
              <div className="form-field">
                <label>Immediate Action Taken *</label>
                <textarea
                  rows={3}
                  required
                  value={corrDesc}
                  onChange={(e) => setCorrDesc(e.target.value)}
                  placeholder="e.g. Faulty component replaced immediately / new tag affixed..."
                />
              </div>
              <div className="form-field">
                <label>Interim Containment Measure</label>
                <input
                  type="text"
                  value={corrContainment}
                  onChange={(e) => setCorrContainment(e.target.value)}
                  placeholder="e.g. 24h roving technician posted for observation."
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                onClick={() => setShowAddCorrection(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary"
              >
                Save Correction
              </button>
            </div>
          </form>
        </div>
      )}

      {/* MODAL: Add Root Cause */}
      {showAddRootCause && (
        <div className="modal-backdrop">
          <form onSubmit={handleAddRootCause} className="modal-card max-w-lg">
            <div className="modal-header">
              <div>
                <h3>Root Cause Analysis (RCA)</h3>
                <p>Identify underlying systematic factors and causal categorization</p>
              </div>
              <button type="button" onClick={() => setShowAddRootCause(false)} className="close-btn">✕</button>
            </div>
            <div className="modal-body space-y-4">
              <div className="form-grid-2">
                <div className="form-field">
                  <label>RCA Methodology</label>
                  <select
                    value={rcMethod}
                    onChange={(e) => setRcMethod(e.target.value as any)}
                  >
                    <option value="FIVE_WHY">5-Whys Analysis</option>
                    <option value="FISHBONE">Ishikawa (Fishbone)</option>
                    <option value="BARRIER_ANALYSIS">Barrier Analysis</option>
                    <option value="HFACS">HFACS</option>
                  </select>
                </div>
                <div className="form-field">
                  <label>Root Cause Category</label>
                  <select
                    value={rcType}
                    onChange={(e) => setRcType(e.target.value)}
                  >
                    <option value="PROCESS">Process / Procedure</option>
                    <option value="PEOPLE">Human Factors / Competence</option>
                    <option value="TRAINING">Training / Qualification</option>
                    <option value="EQUIPMENT">Tooling / Equipment</option>
                    <option value="DOCUMENTATION">Documentation / Guidance</option>
                    <option value="MANAGEMENT">Supervision / Governance</option>
                  </select>
                </div>
              </div>
              <div className="form-field">
                <label>Root Cause Statement *</label>
                <textarea
                  rows={4}
                  required
                  value={rcDesc}
                  onChange={(e) => setRcDesc(e.target.value)}
                  placeholder="What is the foundational breakdown that allowed this deficiency to occur?"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                onClick={() => setShowAddRootCause(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary"
              >
                Save Root Cause
              </button>
            </div>
          </form>
        </div>
      )}

      {/* MODAL: Add Verification */}
      {showAddVerification && (
        <div className="modal-backdrop">
          <form onSubmit={handleAddVerification} className="modal-card max-w-lg">
            <div className="modal-header">
              <div>
                <h3>Independent Verification</h3>
                <p>Validate corrective action effectiveness and verify closure criteria</p>
              </div>
              <button type="button" onClick={() => setShowAddVerification(false)} className="close-btn">✕</button>
            </div>
            <div className="modal-body space-y-4">
              <div className="p-3 bg-blue-50 text-blue-900 rounded-lg text-xs border border-blue-200">
                <strong>Segregation of Duties (SoD):</strong> The verifier must be an independent auditor or authority, distinct from the finding assignee.
              </div>
              <div className="form-grid-2">
                <div className="form-field">
                  <label>Verifier Personnel ID / User</label>
                  <input
                    type="text"
                    required
                    value={verifId}
                    onChange={(e) => setVerifId(e.target.value)}
                    placeholder="e.g. AUD-009"
                  />
                </div>
                <div className="form-field">
                  <label>Verification Outcome</label>
                  <select
                    value={verifResult}
                    onChange={(e) => setVerifResult(e.target.value as VerificationResult)}
                  >
                    <option value="EFFECTIVE">EFFECTIVE</option>
                    <option value="PARTIALLY_EFFECTIVE">PARTIALLY EFFECTIVE</option>
                    <option value="INEFFECTIVE">INEFFECTIVE</option>
                    <option value="NOT_VERIFIABLE">NOT VERIFIABLE</option>
                  </select>
                </div>
              </div>
              <div className="form-field">
                <label>Verification Evidence & Sampling Notes *</label>
                <textarea
                  rows={3}
                  required
                  value={verifNotes}
                  onChange={(e) => setVerifNotes(e.target.value)}
                  placeholder="Physical audit performed, 10 samples examined and standard compliance confirmed."
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                onClick={() => setShowAddVerification(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary"
              >
                Approve Verification
              </button>
            </div>
          </form>
        </div>
      )}

      {/* MODAL: Extension Request */}
      {showExtensionModal && (
        <div className="modal-backdrop">
          <form onSubmit={handleRequestExtension} className="modal-card max-w-lg">
            <div className="modal-header">
              <div>
                <h3>Request Due Date Extension</h3>
                <p>Submit formal request with operational justification for deadline deferral</p>
              </div>
              <button type="button" onClick={() => setShowExtensionModal(false)} className="modal-close-btn" title="Kapat" aria-label="Kapat">
                <X size={18} />
              </button>
            </div>
            <div className="modal-body space-y-4">
              <div className="form-field">
                <label>Current Due Date</label>
                <input
                  type="text"
                  disabled
                  value={finding.dueDate}
                  className="bg-gray-100 text-gray-500 font-mono"
                />
              </div>
              <div className="form-field">
                <label>Requested New Due Date *</label>
                <input
                  type="date"
                  required
                  value={extNewDate}
                  onChange={(e) => setExtNewDate(e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Justification for Extension *</label>
                <textarea
                  rows={3}
                  required
                  value={extReason}
                  onChange={(e) => setExtReason(e.target.value)}
                  placeholder="Supply chain delay, maintenance slot rescheduling, or regulatory waiting period..."
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                onClick={() => setShowExtensionModal(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary"
              >
                Submit Extension Request
              </button>
            </div>
          </form>
        </div>
      )}

      {/* MODAL: Close Finding */}
      {showCloseModal && (
        <div className="modal-backdrop">
          <div className="modal-card max-w-lg">
            <div className="modal-header">
              <div>
                <h3>Formal Closure</h3>
                <p>Finalize and lock finding record upon verified corrective resolution</p>
              </div>
              <button type="button" onClick={() => setShowCloseModal(false)} className="modal-close-btn" title="Kapat" aria-label="Kapat">
                <X size={18} />
              </button>
            </div>
            <div className="modal-body space-y-4">
              <p className="text-sm text-gray-600">
                Once closed, this finding record will transition to read-only status. Prerequisites (accepted correction and effective verification) will be verified.
              </p>
              <div className="form-field">
                <label>Closure Comments / Manager Approval Notes *</label>
                <textarea
                  rows={3}
                  required
                  value={closeComment}
                  onChange={(e) => setCloseComment(e.target.value)}
                  placeholder="Confirming all corrective steps and verification records have met standard criteria..."
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                onClick={() => setShowCloseModal(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCloseFinding}
                disabled={loading}
                className="btn-primary"
              >
                Complete Closure
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Reopen Finding */}
      {showReopenModal && (
        <div className="modal-backdrop">
          <div className="modal-card max-w-lg">
            <div className="modal-header">
              <div>
                <h3 className="text-red-700">Reopen Finding</h3>
                <p>Reinstate finding due to recurrence or ineffective remediation</p>
              </div>
              <button type="button" onClick={() => setShowReopenModal(false)} className="modal-close-btn" title="Kapat" aria-label="Kapat">
                <X size={18} />
              </button>
            </div>
            <div className="modal-body space-y-4">
              <p className="text-sm text-gray-600">
                Used when a previously closed deficiency re-emerges during follow-up audits or when verification evidence is invalidated.
              </p>
              <div className="form-field">
                <label>Reason for Reopening *</label>
                <textarea
                  rows={3}
                  required
                  value={reopenReason}
                  onChange={(e) => setReopenReason(e.target.value)}
                  placeholder="e.g. Subsequent spot inspection indicated recurrence of non-compliance."
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                onClick={() => setShowReopenModal(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleReopenFinding}
                disabled={loading || !reopenReason.trim()}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium text-sm transition"
              >
                Reopen Finding
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
